import { Box, TextField, InputAdornment, Typography } from "@mui/material";
import {
  getBlankCompoundUnits,
  getBlankUnitMeta,
  joinBlankUnitValue,
  stripBlankUnitDisplay,
} from "../../../utils/fillQuestionUtils";
import { sanitizeTypedAnswerWithGivenUnit } from "../../../utils/answerMatchUtils";
import { parseBlankCompoundParts, combineBlankCompoundAnswer, CURRENCY_UNITS } from "../../../utils/answerUnitUtils";

/**
 * Multi-fill / table / fill-in-the-blank TextField with the same £ / % unit chip
 * pattern as short-answer TypeDrawInput (unit shown as adornment, joined into stored value).
 */
export default function FillBlankUnitTextField({
  question,
  blankIdx,
  value,
  onChange,
  onFocus,
  inputRef,
  sx,
  placeholder,
  size = "small",
  inputProps,
  ...rest
}) {
  const compoundUnits = getBlankCompoundUnits(question, blankIdx);
  const unitMeta = getBlankUnitMeta(question, blankIdx);
  const displayValue = stripBlankUnitDisplay(value, unitMeta);

  if (compoundUnits) {
    const parts = parseBlankCompoundParts(value, compoundUnits);
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        {compoundUnits.map((unit, i) => {
          const isPrefix = CURRENCY_UNITS.includes(unit);
          const chip = (
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary", userSelect: "none" }}>
              {unit}
            </Typography>
          );
          return (
            // Keyed by index, not unit: a compound may legitimately repeat a unit name.
            <Box key={i} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
              {isPrefix ? chip : null}
              <TextField
                size={size}
                sx={{ width: "7ch" }}
                inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
                inputRef={i === 0 ? inputRef : undefined}
                onFocus={onFocus}
                value={parts[i] ?? ""}
                onChange={(e) => {
                  const next = [...parts];
                  next[i] = sanitizeTypedAnswerWithGivenUnit(e.target.value, unit, { isPrefix });
                  onChange?.(combineBlankCompoundAnswer(next, compoundUnits));
                }}
                {...rest}
              />
              {isPrefix ? null : chip}
            </Box>
          );
        })}
      </Box>
    );
  }

  const handleChange = (next) => {
    const cleaned = unitMeta?.unit
      ? sanitizeTypedAnswerWithGivenUnit(next, unitMeta.unit, { isPrefix: !!unitMeta.prefix })
      : next;
    onChange?.(joinBlankUnitValue(cleaned, unitMeta));
  };

  return (
    <TextField
      size={size}
      placeholder={placeholder}
      value={displayValue}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={onFocus}
      inputRef={inputRef}
      sx={sx}
      inputProps={inputProps}
      InputProps={{
        ...(unitMeta.prefix
          ? {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary", userSelect: "none" }}>
                    {unitMeta.unit}
                  </Typography>
                </InputAdornment>
              ),
            }
          : {}),
        ...(unitMeta.unit && !unitMeta.prefix
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary", userSelect: "none" }}>
                    {unitMeta.unit}
                  </Typography>
                </InputAdornment>
              ),
            }
          : {}),
      }}
      {...rest}
    />
  );
}

/** Display/value helpers for MathInputTools wired to a unit-aware blank. */
export function blankMathToolsAdapters(question, blankIdx, storedValue, onChange) {
  const compoundUnits = getBlankCompoundUnits(question, blankIdx);
  if (compoundUnits) {
    // Each compound box holds a bare number; the maths keypad edits the first one.
    return {
      getValue: () => parseBlankCompoundParts(storedValue, compoundUnits)[0] ?? "",
      onChange: (next) => {
        const parts = parseBlankCompoundParts(storedValue, compoundUnits);
        parts[0] = sanitizeTypedAnswerWithGivenUnit(next, compoundUnits[0], {
          isPrefix: CURRENCY_UNITS.includes(compoundUnits[0]),
        });
        onChange?.(combineBlankCompoundAnswer(parts, compoundUnits));
      },
    };
  }
  const unitMeta = getBlankUnitMeta(question, blankIdx);
  return {
    getValue: () => stripBlankUnitDisplay(storedValue, unitMeta),
    onChange: (next) => {
      const cleaned = unitMeta?.unit
        ? sanitizeTypedAnswerWithGivenUnit(next, unitMeta.unit, { isPrefix: !!unitMeta.prefix })
        : next;
      onChange?.(joinBlankUnitValue(cleaned, unitMeta));
    },
  };
}
